<template>
  <div class="space-y-6">
    <!-- Live status -->
    <Card>
      <div class="flex items-start justify-between mb-4">
        <div>
          <h3 class="text-lg font-medium text-text">Scheduler Status</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Live concurrency + queue state from the per-org scheduler.
          </p>
        </div>
        <button
          type="button"
          @click="refresh"
          class="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          :disabled="loading"
        >
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>

      <div v-if="error" class="text-sm text-red-600 dark:text-red-400 mb-4">{{ error }}</div>

      <div v-if="status" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active" :value="status.active" :sub="`/${status.current_slots} slots`" />
        <StatCard label="Queued" :value="status.queued" :sub="`/${status.max_queue_size} max`" />
        <StatCard
          label="Cores"
          :value="(status.current_cores_milli / 1000).toFixed(2)"
          :sub="`min ${(status.min_cores_milli / 1000).toFixed(1)} · max ${(status.max_cores_milli / 1000).toFixed(1)}`"
        />
        <StatCard
          label="Overage"
          :value="`$${(status.core_overage_microcents_per_hr / 1_000_000).toFixed(4)}/hr`"
          :sub="status.scheduler_mode === 'live' ? 'live' : 'tier default'"
        />
      </div>

      <div v-if="status" class="mt-4 grid grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
        <div>
          Saturation:
          <span class="font-mono text-gray-900 dark:text-gray-100">
            {{ status.saturation_pct.toFixed(1) }}%
          </span>
        </div>
        <div>
          Admitted total:
          <span class="font-mono text-gray-900 dark:text-gray-100">
            {{ status.admitted_total.toLocaleString() }}
          </span>
        </div>
        <div>
          Queue load:
          <span class="font-mono text-gray-900 dark:text-gray-100">
            {{ status.queue_pct.toFixed(1) }}%
          </span>
        </div>
        <div>
          Rejected (queue_full):
          <span class="font-mono text-gray-900 dark:text-gray-100">
            {{ status.rejected_queue_full.toLocaleString() }}
          </span>
        </div>
      </div>
    </Card>

    <!-- Editable settings — policy + concurrency + queue depth -->
    <Card class="space-y-6">
      <div>
        <h3 class="text-lg font-medium text-text">Scheduler Settings</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Owner or admin may edit. Lazy propagation — new admits pick up changes;
          queued tasks keep old values until drained.
        </p>
      </div>

      <!-- Policy -->
      <fieldset :disabled="!canEdit || saving" class="space-y-3">
        <legend class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Policy</legend>
        <label
          v-for="opt in policyOptions"
          :key="opt.value"
          class="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer"
          :class="
            form.scheduler_policy === opt.value
              ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          "
        >
          <Radio
            name="scheduler_policy"
            :value="opt.value"
            :model-value="form.scheduler_policy"
            class="mt-1"
            @update:model-value="form.scheduler_policy = $event as SchedulerPolicy"
          />
          <div>
            <div class="font-medium text-gray-900 dark:text-white">{{ opt.label }}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ opt.desc }}</div>
          </div>
        </label>
      </fieldset>

      <!-- Concurrency (cores) -->
      <fieldset :disabled="!canEdit || saving" class="space-y-3">
        <legend class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Concurrency</legend>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          1 core = 50 execution slots. Tier baseline is enforced as minimum; platform ceiling caps max at 40 cores.
        </p>
        <div class="grid grid-cols-2 gap-4">
          <label class="block">
            <span class="text-xs text-gray-500 dark:text-gray-400 uppercase">
              Min cores ({{ form.min_cores_milli }} milli)
            </span>
            <input
              type="number"
              v-model.number="form.min_cores_milli"
              :min="tierBaselineMilli"
              :max="form.max_cores_milli"
              step="200"
              class="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white"
            />
            <span class="mt-1 block text-xs text-gray-500">
              {{ (form.min_cores_milli / 1000).toFixed(2) }} cores · baseline = {{ (tierBaselineMilli / 1000).toFixed(1) }}
            </span>
          </label>
          <label class="block">
            <span class="text-xs text-gray-500 dark:text-gray-400 uppercase">
              Max cores ({{ form.max_cores_milli }} milli)
            </span>
            <input
              type="number"
              v-model.number="form.max_cores_milli"
              :min="form.min_cores_milli"
              :max="40000"
              step="200"
              class="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white"
            />
            <span class="mt-1 block text-xs text-gray-500">
              {{ (form.max_cores_milli / 1000).toFixed(2) }} cores · auto-scale ceiling
            </span>
          </label>
        </div>
      </fieldset>

      <!-- Queue depth -->
      <fieldset :disabled="!canEdit || saving">
        <legend class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Queue Depth</legend>
        <label class="block">
          <input
            type="number"
            v-model.number="form.max_queue_size"
            :min="1"
            :max="10000"
            step="10"
            class="w-full sm:w-48 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white"
          />
          <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            Max pending tasks after concurrency saturates. Default = 2 × current slots. Excess → 429 queue_full.
          </span>
        </label>
      </fieldset>

      <!-- Actions -->
      <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
        <p v-if="!canEdit" class="text-xs text-gray-500 dark:text-gray-400">
          Owner or admin role required to edit.
        </p>
        <p v-else-if="dirty" class="text-xs text-amber-600 dark:text-amber-400">
          Unsaved changes.
        </p>
        <p v-else class="text-xs text-gray-400">No changes.</p>
        <div class="flex gap-2">
          <Button type="button" variant="outline" :disabled="!canEdit || saving || !dirty" @click="resetForm">
            Reset
          </Button>
          <Button type="button" :loading="saving" :disabled="!canEdit || saving || !dirty" @click="save">
            {{ saving ? 'Saving…' : 'Save' }}
          </Button>
        </div>
      </div>
    </Card>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch } from 'vue';
  import { organizationsApi } from '../api/organizations';
  import { useOrganization, useOrganizationPermissions } from '../composables';
  import type { SchedulerPolicy, SchedulerStatus, UpdateOrganizationRequest } from '../types';
  import StatCard from './SchedulerStatCard.vue';
  import { Card, Button, Radio } from '@asabdev/asab-ui';

  // Tier baselines mirror asab-auth/tiers (free=200, pro=2000, ent=4000).
  const TIER_BASELINE_MILLI: Record<string, number> = {
    free: 200,
    pro: 2000,
    enterprise: 4000,
  };

  // Product rule: max queue depth cannot exceed 5 × current cores. Prevents
  // orgs from hoarding unbounded work during a traffic spike at baseline cost.
  const QUEUE_MULTIPLIER = 5;

  const { organization, updateOrganization } = useOrganization();
  const { isOwner, isAdmin } = useOrganizationPermissions();

  const status = ref<SchedulerStatus | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<string | null>(null);

  // Owner OR admin may edit. API keys are capped at member at the gateway,
  // so only interactive JWT sessions can reach this save path.
  const canEdit = computed(() => isOwner.value || isAdmin.value);

  // Editable form state — seeded from organization + live status.
  const form = reactive({
    scheduler_policy: 'fifo' as SchedulerPolicy,
    min_cores_milli: 200,
    max_cores_milli: 200,
    max_queue_size: 20,
  });

  const tier = computed(() => organization.value?.tier || 'free');
  const tierBaselineMilli = computed(() => TIER_BASELINE_MILLI[tier.value] ?? 200);

  const policyOptions: Array<{ value: SchedulerPolicy; label: string; desc: string }> = [
    {
      value: 'fifo',
      label: 'FIFO (default)',
      desc: 'Queued tasks popped in enqueue order. Simple and fair.',
    },
    {
      value: 'priority',
      label: 'Priority',
      desc: 'Uses per-agent priority. Higher-priority agents pop first; same priority = FIFO.',
    },
  ];

  // Client-side ceilings enforced inline. Server validates authoritatively.
  const queueCeiling = computed(() => {
    const curSlots = Math.floor((form.min_cores_milli * 50) / 1000);
    // Queue cap scales with provisioned (min) cores, NOT max. Otherwise orgs
    // could queue 5 × max-auto-scaled workload without paying for steady-state.
    return Math.max(1, curSlots * QUEUE_MULTIPLIER);
  });

  function hydrateForm() {
    const o = organization.value;
    if (!o) return;
    form.scheduler_policy = (o.scheduler_policy as SchedulerPolicy) || 'fifo';
    form.min_cores_milli = o.min_cores_milli ?? tierBaselineMilli.value;
    form.max_cores_milli = o.max_cores_milli ?? tierBaselineMilli.value;
    form.max_queue_size = o.max_queue_size ?? (tierBaselineMilli.value * 50 / 1000) * 2;
  }

  const dirty = computed(() => {
    const o = organization.value;
    if (!o) return false;
    return (
      form.scheduler_policy !== (o.scheduler_policy || 'fifo') ||
      form.min_cores_milli !== (o.min_cores_milli ?? tierBaselineMilli.value) ||
      form.max_cores_milli !== (o.max_cores_milli ?? tierBaselineMilli.value) ||
      form.max_queue_size !== (o.max_queue_size ?? (tierBaselineMilli.value * 50 / 1000) * 2)
    );
  });

  async function refresh() {
    if (!organization.value?.id) return;
    loading.value = true;
    error.value = null;
    try {
      status.value = await organizationsApi.getSchedulerStatus(organization.value.id);
    } catch (e) {
      error.value = (e as Error).message || 'failed to load scheduler status';
    } finally {
      loading.value = false;
    }
  }

  function validate(): string | null {
    if (form.min_cores_milli < tierBaselineMilli.value) {
      return `Min cores below tier "${tier.value}" baseline (${tierBaselineMilli.value} milli).`;
    }
    if (form.max_cores_milli < form.min_cores_milli) {
      return 'Max cores must be ≥ min cores.';
    }
    if (form.max_cores_milli > 40_000) {
      return 'Max cores above platform ceiling (40 000 milli / 2 000 slots).';
    }
    if (form.max_queue_size < 1) {
      return 'Max queue size must be ≥ 1.';
    }
    if (form.max_queue_size > queueCeiling.value) {
      return `Max queue size (${form.max_queue_size}) exceeds 5 × current cores (${queueCeiling.value}).`;
    }
    return null;
  }

  async function save() {
    if (!organization.value?.id) return;
    const err = validate();
    if (err) {
      error.value = err;
      return;
    }
    saving.value = true;
    error.value = null;
    try {
      const payload: UpdateOrganizationRequest = {
        scheduler_policy: form.scheduler_policy,
        min_cores_milli: form.min_cores_milli,
        max_cores_milli: form.max_cores_milli,
        max_queue_size: form.max_queue_size,
      };
      await updateOrganization(payload);
      await refresh();
    } catch (e) {
      error.value = (e as Error).message || 'failed to save scheduler settings';
    } finally {
      saving.value = false;
    }
  }

  function resetForm() {
    hydrateForm();
    error.value = null;
  }

  watch(organization, hydrateForm, { deep: true });

  onMounted(async () => {
    hydrateForm();
    await refresh();
  });
</script>
